import { useMemo } from 'react';
import useSWR from 'swr';

import BookingService from 'services/BookingService';
import { addDays, formatDate, parseUTCDate } from 'utils/dateHelpers';
import getTimeZone from 'utils/getTimeZone';
import { isUserTraveller } from 'utils/isRole';
import removePreviousSchedules from 'utils/removePreviousSchedules';

import useUnreadChats from './useUnreadChats';

const LOOKAHEAD_DAYS = 30;

/**
 * A booking still waiting on this user's briefing.
 *
 * The traveller fills `hasBriefing`; the advisor fills `hasAdvisorBriefing`.
 * Cancelled bookings are dropped — there is nothing left to prepare for.
 */
export function selectPendingBriefings(schedules, isTraveller) {
  if (!Array.isArray(schedules)) return [];

  return schedules.filter((schedule) => {
    if (!schedule || schedule.canceled) return false;
    return isTraveller ? !schedule.hasBriefing : !schedule.hasAdvisorBriefing;
  });
}

/** The soonest booking that has not started yet. */
export function selectNextMeeting(schedules) {
  if (!Array.isArray(schedules) || schedules.length === 0) return null;

  return (
    schedules
      .filter(
        (schedule) => schedule && !schedule.canceled && schedule.startAtUTC
      )
      .sort(
        (a, b) => parseUTCDate(a.startAtUTC) - parseUTCDate(b.startAtUTC)
      )[0] || null
  );
}

/**
 * Everything the user still has to act on, built entirely from endpoints that
 * already exist: the upcoming agenda and the chat list.
 *
 * Not covered: pending ratings. Deciding whether a past meeting was already
 * rated needs one `GET /assessment/{bookingId}` per booking, and the API has no
 * bulk equivalent — so it is left out rather than fired in a loop.
 */
export default function usePendingActions(userRole) {
  const isTraveller = isUserTraveller(userRole);
  const { unreadCount } = useUnreadChats(!!userRole);

  const queryParams = useMemo(() => {
    const now = new Date();
    return {
      startsAt: formatDate(now, 'yyyy-MM-dd'),
      endsAt: formatDate(addDays(now, LOOKAHEAD_DAYS), 'yyyy-MM-dd'),
      timeZone: getTimeZone(),
      size: 50,
      direction: 'ASC',
    };
  }, []);

  const { data, error, isLoading } = useSWR(
    userRole ? ['pending-actions', userRole] : null,
    () =>
      BookingService.getBookingScheduledUser({
        role: userRole,
        params: queryParams,
      })
  );

  const upcoming = useMemo(
    () =>
      removePreviousSchedules({
        schedules: data?.content || [],
        isTraveller,
      }),
    [data, isTraveller]
  );

  const pendingBriefings = useMemo(
    () => selectPendingBriefings(upcoming, isTraveller),
    [upcoming, isTraveller]
  );

  const nextMeeting = useMemo(() => selectNextMeeting(upcoming), [upcoming]);

  return {
    error,
    isLoading,
    nextMeeting,
    pendingBriefings,
    totalCount: pendingBriefings.length + unreadCount,
    unreadCount,
    upcoming,
  };
}
